import { getLocationName, resolveCityLocationId } from '../api/locationApi';
import { getCountryCodeForCity } from '../api/regionWaitlistApi';
import { getSupabase, isSupabaseConfigured } from './supabase';
import {
  loadActiveSource as loadLegacyActiveSource,
  loadGpsCache,
  loadInterestPick,
  loadRegions,
} from './onboardingStorage';

const LS_GPS_SLOT = 'mingo_gps_slot';
const LS_PICK_SLOT = 'mingo_pick_slot';
const LS_ACTIVE = 'mingo_active';
const LS_MIGRATED = 'mingo_region_slots_migrated_v1';

export const COLDSTART_DEFAULT_CITY = Object.freeze({
  country_code: 'VN',
  city_label: '다낭',
});

/**
 * @typedef {Object} LocationValue
 * @property {string} country_code — ISO-2 (필수)
 * @property {string} city_id — locations.id (필수)
 * @property {string} [district_id]
 * @property {string} [city_label] — UI 표시명 (fetch 실패 시 폴백)
 * @property {string} [city_code] — locations.code (danang 등)
 * @property {string} [neighborhood_id]
 * @property {number} [lat]
 * @property {number} [lng]
 * @property {number} [radius_km]
 */

/** @typedef {'gps' | 'pick'} ActiveSource */

/**
 * @param {unknown} raw
 * @returns {LocationValue | null}
 */
export function normalizeLocationValue(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const country_code = String(raw.country_code || '')
    .trim()
    .toUpperCase()
    .slice(0, 2);
  const city_id = String(raw.city_id || '').trim();
  if (!country_code || !city_id) return null;

  /** @type {LocationValue} */
  const out = { country_code, city_id };

  if (raw.district_id != null && String(raw.district_id).trim()) {
    out.district_id = String(raw.district_id).trim();
  }
  if (raw.city_label != null && String(raw.city_label).trim()) {
    out.city_label = String(raw.city_label).trim();
  }
  if (raw.city_code != null && String(raw.city_code).trim()) {
    out.city_code = String(raw.city_code).trim().toLowerCase();
  }
  if (raw.neighborhood_id != null && String(raw.neighborhood_id).trim()) {
    out.neighborhood_id = String(raw.neighborhood_id).trim();
  }
  if (raw.lat != null && Number.isFinite(Number(raw.lat))) out.lat = Number(raw.lat);
  if (raw.lng != null && Number.isFinite(Number(raw.lng))) out.lng = Number(raw.lng);
  if (raw.radius_km != null && Number.isFinite(Number(raw.radius_km))) {
    out.radius_km = Number(raw.radius_km);
  }

  return out;
}

/**
 * @param {LocationValue | null | undefined} value
 * @returns {boolean}
 */
export function isValidLocationValue(value) {
  return Boolean(value?.country_code && value?.city_id);
}

/**
 * locations row (findNearestLocation 등) → LocationValue
 * @param {object | null | undefined} locationRow
 * @param {{ lat?: number; lng?: number; radius_km?: number; country_code?: string }} [opts]
 * @returns {LocationValue | null}
 */
export function locationValueFromLocationRow(locationRow, opts = {}) {
  if (!locationRow?.id) return null;
  const country_code = String(
    locationRow.country_code || opts.country_code || 'VN',
  )
    .trim()
    .toUpperCase()
    .slice(0, 2);

  return normalizeLocationValue({
    country_code,
    city_id: String(locationRow.id),
    city_code: locationRow.code ? String(locationRow.code).trim().toLowerCase() : undefined,
    district_id: locationRow.level === 'district' ? String(locationRow.id) : undefined,
    lat: opts.lat ?? locationRow.lat,
    lng: opts.lng ?? locationRow.lng,
    radius_km: opts.radius_km,
  });
}

/**
 * districts row가 구 단위인지 (도시명과 구명이 다름)
 * @param {object | null | undefined} row
 */
function isDistinctDistrictRow(row) {
  if (!row) return false;
  const city = String(row.city_en || row.city_ko || '').trim().toLowerCase();
  const district = String(row.district_en || row.district_ko || '').trim().toLowerCase();
  return Boolean(district && city && district !== city);
}

/**
 * districts 검색 row + city context → LocationValue (city_id 필수 resolve)
 * @param {object} districtRow
 * @param {string} cityLabel
 * @param {{ radius_km?: number; country_code?: string; city_id?: string }} [opts]
 * @returns {Promise<LocationValue | null>}
 */
export async function locationValueFromDistrictPick(districtRow, cityLabel, opts = {}) {
  const country_code = String(
    opts.country_code || districtRow?.country_code || getCountryCodeForCity(cityLabel) || 'VN',
  )
    .trim()
    .toUpperCase()
    .slice(0, 2);

  const label = String(cityLabel || '').trim();

  let city_id = opts.city_id ? String(opts.city_id) : '';

  // City-level districts rows often already carry locations.id — use before name resolve
  // (gated/not-yet-open cities may be missing from locations.level=city listing).
  if (!city_id && districtRow?.location_id != null && !isDistinctDistrictRow(districtRow)) {
    city_id = String(districtRow.location_id);
  }

  if (!city_id) {
    const candidates = [
      label,
      districtRow?.city_en,
      districtRow?.city_ko,
      districtRow?.city_vi,
      districtRow?.city_ru,
    ]
      .map((v) => String(v || '').trim())
      .filter(Boolean);
    for (const cand of candidates) {
      const resolved = await resolveCityLocationId(cand, {
        countryCode: country_code,
        lat: districtRow?.lat,
        lng: districtRow?.lng,
        fallbackLabel: label || cand,
      });
      if (resolved != null) {
        city_id = String(resolved);
        break;
      }
    }
  }

  if (!city_id) return null;

  // districtRow.id는 검색 테이블(districts)의 자체 PK라서 locations.id와 다른 값이었음.
  // fetchSlotLabel 등에서 이걸로 locations를 재조회하면 항상 0건이 되어(구/군이 매번 누락)
  // "충청북도"처럼 상위 도시명만 남는 버그의 원인이었음. locations.id로 연결된
  // location_id(신규 컬럼)가 있을 때만 district_id를 채운다.
  return normalizeLocationValue({
    country_code,
    city_id,
    district_id:
      districtRow?.location_id != null && isDistinctDistrictRow(districtRow)
        ? String(districtRow.location_id)
        : undefined,
    lat: districtRow?.lat,
    lng: districtRow?.lng,
    radius_km: opts.radius_km,
    city_label: label,
  });
}

function readJson(key) {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeJson(key, value) {
  if (typeof window === 'undefined') return;
  try {
    if (value == null) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

/** @returns {LocationValue | null} */
export function loadGpsSlot() {
  return normalizeLocationValue(readJson(LS_GPS_SLOT));
}

/** @param {LocationValue | null} slot */
export function saveGpsSlot(slot) {
  writeJson(LS_GPS_SLOT, isValidLocationValue(slot) ? slot : null);
}

/** @returns {LocationValue | null} */
export function loadPickSlot() {
  return normalizeLocationValue(readJson(LS_PICK_SLOT));
}

/** @param {LocationValue | null} slot */
export function savePickSlot(slot) {
  if (!isValidLocationValue(slot)) {
    writeJson(LS_PICK_SLOT, null);
    return;
  }
  const normalized = normalizeLocationValue(slot);
  writeJson(LS_PICK_SLOT, normalized);
}

/** @returns {ActiveSource} */
export function loadActive() {
  if (typeof window === 'undefined') return 'gps';
  const v = window.localStorage.getItem(LS_ACTIVE);
  return v === 'pick' ? 'pick' : 'gps';
}

/** @param {ActiveSource} source */
export function saveActive(source) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_ACTIVE, source === 'pick' ? 'pick' : 'gps');
}

/**
 * @param {LocationValue | null} gpsSlot
 * @param {LocationValue | null} pickSlot
 * @param {ActiveSource} activeSource
 * @returns {LocationValue | null}
 */
export function getActiveRegion(gpsSlot, pickSlot, activeSource, options = {}) {
  const allowFallback = options.allowFallback !== false;
  const preferred = activeSource === 'pick' ? pickSlot : gpsSlot;
  const fallback = activeSource === 'pick' ? gpsSlot : pickSlot;
  if (isValidLocationValue(preferred)) return preferred;
  if (!allowFallback) return null;
  if (isValidLocationValue(fallback)) return fallback;
  return null;
}

/**
 * GPS 슬롯이 비었을 때 콜드스타트 기본 도시로 채운다.
 * @param {{ radius_km?: number }} [opts]
 * @returns {Promise<LocationValue | null>}
 */
export async function buildColdstartGpsSlot(opts = {}) {
  const countryCode = COLDSTART_DEFAULT_CITY.country_code;
  const cityLabel = COLDSTART_DEFAULT_CITY.city_label;
  const cityId = await resolveCityLocationId(cityLabel, {
    countryCode,
    fallbackLabel: cityLabel,
  });
  if (cityId == null) return null;

  // lat/lng를 채워두지 않으면 날씨 위젯(resolveWeatherPlace)이 좌표 없는 슬롯을 받아
  // 엉뚱한 위치(0,0) 날씨를 보여주는 버그가 있었음 — 위치초기화 직후에도 반드시 좌표를 채운다.
  let lat;
  let lng;
  if (isSupabaseConfigured) {
    const sb = getSupabase();
    if (sb) {
      const { data } = await sb
        .from('locations')
        .select('lat, lng')
        .eq('id', cityId)
        .eq('is_active', true)
        .maybeSingle();
      if (data?.lat != null && data?.lng != null) {
        lat = Number(data.lat);
        lng = Number(data.lng);
      }
    }
  }

  return normalizeLocationValue({
    country_code: countryCode,
    city_id: String(cityId),
    city_label: cityLabel,
    lat,
    lng,
    radius_km: Number.isFinite(Number(opts.radius_km)) ? Number(opts.radius_km) : undefined,
  });
}

/**
 * fetch된 라벨 또는 slot.city_label 폴백
 * @param {LocationValue | null | undefined} slot
 * @param {string} [fetchedLabel]
 * @returns {string}
 */
export function effectiveSlotLabel(slot, fetchedLabel = '') {
  const fetched = String(fetchedLabel || '').trim();
  if (fetched) return fetched;
  return String(slot?.city_label || '').trim();
}

/**
 * city_id(+district_id) → locations row → { cityLabel, districtLabel } (언어별 표시명)
 * @param {LocationValue | null | undefined} slot
 * @param {string} [language]
 * @param {object | null} [cachedLocationRow] 이미 로드된 locations row (GPS 측위 직후)
 * @returns {Promise<{ cityLabel: string, districtLabel: string }>}
 */
async function fetchSlotLabelParts(slot, language = 'ko', cachedLocationRow = null) {
  if (!isValidLocationValue(slot)) return { cityLabel: '', districtLabel: '' };

  const storedLabel = String(slot.city_label || '').trim();

  let cityLabel = storedLabel;
  if (cachedLocationRow && String(cachedLocationRow.id) === String(slot.city_id)) {
    cityLabel = getLocationName(cachedLocationRow, language) || storedLabel;
  } else if (isSupabaseConfigured) {
    const sb = getSupabase();
    if (sb) {
      const { data } = await sb
        .from('locations')
        .select('*')
        .eq('id', slot.city_id)
        .eq('is_active', true)
        .maybeSingle();
      if (data) cityLabel = getLocationName(data, language) || storedLabel;
    }
  }

  let districtLabel = '';
  // district_id(구/군/동)가 있으면 도시명만 반환하지 않고 "도시 · 구/군" 형태로 합성
  // (전남광주특별시 · 나주시 처럼 상위 city와 실제 선택한 district가 다른 경우 district가 누락되던 버그 수정)
  if (slot.district_id && isSupabaseConfigured) {
    const sb = getSupabase();
    if (sb) {
      const { data: distData } = await sb
        .from('locations')
        .select('*')
        .eq('id', slot.district_id)
        .eq('is_active', true)
        .maybeSingle();
      if (distData) {
        const distLabel = getLocationName(distData, language);
        if (distLabel && distLabel.trim().toLowerCase() !== cityLabel.trim().toLowerCase()) {
          districtLabel = distLabel;
        }
      }
    }
  }

  return { cityLabel, districtLabel };
}

/**
 * city_id → locations row → 언어별 표시명 ("도시 · 구/군" 조합, 헤더/브레드크럼용)
 * @param {LocationValue | null | undefined} slot
 * @param {string} [language]
 * @param {object | null} [cachedLocationRow] 이미 로드된 locations row (GPS 측위 직후)
 * @returns {Promise<string>}
 */
export async function fetchSlotLabel(slot, language = 'ko', cachedLocationRow = null) {
  const { cityLabel, districtLabel } = await fetchSlotLabelParts(slot, language, cachedLocationRow);
  if (districtLabel) return `${cityLabel} · ${districtLabel}`;
  return cityLabel;
}

/**
 * 가장 구체적인 지명 하나만 필요한 곳(날씨 위젯 등)에서 사용.
 * district(구/군/동)가 있으면 그 이름만, 없으면 도시명.
 * @param {LocationValue | null | undefined} slot
 * @param {string} [language]
 * @param {object | null} [cachedLocationRow]
 * @returns {Promise<string>}
 */
export async function fetchSlotSpecificLabel(slot, language = 'ko', cachedLocationRow = null) {
  const { cityLabel, districtLabel } = await fetchSlotLabelParts(slot, language, cachedLocationRow);
  return districtLabel || cityLabel;
}

/**
 * @param {LocationValue | null | undefined} slot
 * @param {object | null} locationRow
 * @param {string} [language]
 * @returns {string}
 */
export function slotLabelFromCachedRow(slot, locationRow, language = 'ko') {
  if (!isValidLocationValue(slot) || !locationRow) return '';
  if (String(locationRow.id) !== String(slot.city_id)) return '';
  return getLocationName(locationRow, language);
}

/**
 * Sync hydrate — migration은 별도 async 호출.
 * @returns {{ gpsSlot: LocationValue | null; pickSlot: LocationValue | null; activeSource: ActiveSource }}
 */
export function loadRegionSlotsState() {
  return {
    gpsSlot: loadGpsSlot(),
    pickSlot: loadPickSlot(),
    activeSource: loadActive(),
  };
}

/**
 * legacy mingo_regions / mingo_gps_cache / mingo_interest_pick → 새 슬롯 키 (1회).
 * city_id 없는 legacy 라벨은 resolveCityLocationId로 보강.
 * @returns {Promise<{ gpsSlot: LocationValue | null; pickSlot: LocationValue | null; activeSource: ActiveSource }>}
 */
export async function migrateLegacyRegionStorageOnce() {
  if (typeof window === 'undefined') {
    return { gpsSlot: null, pickSlot: null, activeSource: 'gps' };
  }

  if (window.localStorage.getItem(LS_MIGRATED) === '1') {
    return loadRegionSlotsState();
  }

  let gpsSlot = loadGpsSlot();
  let pickSlot = loadPickSlot();
  let activeSource = loadActive();

  if (!gpsSlot) {
    const cachedGps = loadGpsCache();
    if (cachedGps?.location) {
      gpsSlot = locationValueFromLocationRow(cachedGps.location, {
        lat: cachedGps.coords?.lat,
        lng: cachedGps.coords?.lng,
      });
      if (gpsSlot) saveGpsSlot(gpsSlot);
    }
  }

  if (!pickSlot && !loadPickSlot()) {
    const interest = loadInterestPick();
    if (interest?.city_id) {
      pickSlot = normalizeLocationValue(interest);
    } else if (interest?.city_label) {
      const cc = String(interest.country_code || getCountryCodeForCity(interest.city_label) || 'VN')
        .toUpperCase()
        .slice(0, 2);
      const cityId = await resolveCityLocationId(interest.city_label, { countryCode: cc });
      if (cityId != null) {
        pickSlot = normalizeLocationValue({
          country_code: cc,
          city_id: String(cityId),
          district_id: interest.district_id,
          lat: interest.lat,
          lng: interest.lng,
          radius_km: interest.radius_km,
        });
      }
    }

    if (!pickSlot) {
      const legacy = loadRegions() || [];
      const legacyPickLabel = String(legacy[1] || '').trim();
      if (legacyPickLabel) {
        const cc = getCountryCodeForCity(legacyPickLabel) || 'VN';
        const cityId = await resolveCityLocationId(legacyPickLabel, { countryCode: cc });
        if (cityId != null) {
          pickSlot = normalizeLocationValue({
            country_code: cc,
            city_id: String(cityId),
          });
        }
      }
    }

    if (pickSlot && !loadPickSlot()) savePickSlot(pickSlot);
  }

  const legacyActive = loadLegacyActiveSource();
  if (!window.localStorage.getItem(LS_ACTIVE)) {
    activeSource = legacyActive === 'interest' ? 'pick' : 'gps';
    saveActive(activeSource);
  }

  try {
    window.localStorage.removeItem('mingo_regions');
    window.localStorage.removeItem('selectedRegions');
  } catch {
    /* ignore */
  }

  window.localStorage.setItem(LS_MIGRATED, '1');

  return {
    gpsSlot: loadGpsSlot(),
    pickSlot: loadPickSlot(),
    activeSource: loadActive(),
  };
}

/** 로그아웃·초기화 시 슬롯 키 전부 제거 */
export function clearRegionSlots() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LS_GPS_SLOT);
  window.localStorage.removeItem(LS_PICK_SLOT);
  window.localStorage.removeItem(LS_ACTIVE);
  window.localStorage.removeItem(LS_MIGRATED);
}
